import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useSharedLogic } from "./use-shared-logic";

export type PermissionState = "granted" | "denied" | "prompt";

export type MicrophonePermission = {
	requestPermission: () => Promise<boolean>;
	checkPermission: () => Promise<PermissionState>;
};

function useWebMicrophonePermission(): MicrophonePermission {
	const checkPermission = useCallback(async (): Promise<PermissionState> => {
		if (navigator.permissions) {
			try {
				const status = await navigator.permissions.query({
					name: "microphone" as PermissionName,
				});
				return status.state as PermissionState;
			} catch {
				return "prompt";
			}
		}
		return "prompt";
	}, []);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Error handling requires multiple branches
	const requestPermission = useCallback(async (): Promise<boolean> => {
		const state = await checkPermission();
		if (state === "denied") {
			toast.error(
				"Microphone access blocked. Please reset your browser's site settings to allow microphone access.",
				{ duration: 6000 }
			);
			return false;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});
			for (const track of stream.getTracks()) {
				track.stop();
			}
			return true;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			if (
				errorMessage.includes("NotReadableError") ||
				errorMessage.includes("Could not start audio source")
			) {
				toast.error(
					"Microphone is in use by another application or permission was denied"
				);
			} else if (
				errorMessage.includes("NotAllowedError") ||
				errorMessage.includes("Permission denied")
			) {
				toast.error(
					"Microphone access denied. Please check your browser settings to allow microphone access",
					{ duration: 6000 }
				);
			} else if (errorMessage.includes("NotFoundError")) {
				toast.error(
					"No microphone found. Please connect a microphone and try again"
				);
			}
			return false;
		}
	}, [checkPermission]);

	return useMemo(
		() => ({ requestPermission, checkPermission }),
		[requestPermission, checkPermission]
	);
}

function useElectronMicrophonePermission(): MicrophonePermission {
	const checkPermission = useCallback(async (): Promise<PermissionState> => {
		if (!window.api?.media) return "prompt";
		const status = await window.api.media.checkPermission("microphone");
		if (status === "granted") return "granted";
		if (status === "denied" || status === "restricted") return "denied";
		return "prompt";
	}, []);

	const requestPermission = useCallback(async (): Promise<boolean> => {
		if (!window.api?.media) return false;
		const state = await checkPermission();
		if (state === "denied") {
			toast.error(
				"Microphone access blocked. Please enable microphone access in System Preferences > Security & Privacy > Privacy > Microphone.",
				{ duration: 6000 }
			);
			return false;
		}

		const granted = await window.api.media.requestMicPermission();
		if (!granted) {
			toast.error(
				"Microphone access denied. Please enable microphone access in System Preferences.",
				{ duration: 6000 }
			);
		}
		return granted;
	}, [checkPermission]);

	return useMemo(
		() => ({ requestPermission, checkPermission }),
		[requestPermission, checkPermission]
	);
}

export function useMicrophonePermission(): MicrophonePermission {
	return useSharedLogic({
		web: useWebMicrophonePermission,
		electron: useElectronMicrophonePermission,
	}) as MicrophonePermission;
}
